import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getErrorMessage, checkConnection } from './errorHandler';

const COLLECTION = 'payments';

/**
 * Registrar um pagamento ao concluir um agendamento.
 * @param {object} paymentData - Dados capturados pelo PaymentModal
 */
export const createPayment = async (paymentData) => {
  try {
    if (!checkConnection()) {
      return { success: false, error: 'Sem conexão com a internet' };
    }

    const docRef = await addDoc(collection(db, COLLECTION), {
      ...paymentData,
      createdAt: paymentData.createdAt || new Date().toISOString(),
    });

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating payment:', error);
    return { success: false, error: getErrorMessage(error) };
  }
};

/**
 * Buscar todos os pagamentos de uma barbearia.
 * @param {string} barbershopId
 */
export const getPaymentsByBarbershop = async (barbershopId) => {
  try {
    if (!checkConnection()) {
      return { success: false, error: 'Sem conexão com a internet', data: [] };
    }

    const q = query(
      collection(db, COLLECTION),
      where('barbershopId', '==', barbershopId),
      orderBy('paymentDate', 'desc')
    );

    const snapshot = await getDocs(q);
    const payments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    return { success: true, data: payments };
  } catch (error) {
    console.error('Error fetching payments:', error);
    return { success: false, error: getErrorMessage(error), data: [] };
  }
};

/**
 * Calcular métricas financeiras a partir de um array de pagamentos.
 * Retorna totais por forma de pagamento, média por atendimento e total geral.
 * @param {Array} payments
 */
export const computePaymentMetrics = (payments) => {
  const totals = { cash: 0, pix: 0, card: 0 };
  const counts = { cash: 0, pix: 0, card: 0 };
  const cardSubtotals = { debit: 0, credit: 0 };
  let grandTotal = 0;

  payments.forEach(p => {
    const method = p.paymentMethod;
    // Valor efetivo recebido (com juros se houver)
    const effective =
      method === 'card' && p.cardHasInterest && p.cardFinalAmount != null
        ? p.cardFinalAmount
        : p.amountCharged;

    totals[method] = (totals[method] || 0) + effective;
    counts[method] = (counts[method] || 0) + 1;
    grandTotal += effective;

    if (method === 'card') {
      cardSubtotals[p.cardType] = (cardSubtotals[p.cardType] || 0) + effective;
    }
  });

  const avgPerService = payments.length > 0 ? grandTotal / payments.length : 0;

  return {
    grandTotal,
    avgPerService,
    totals,
    counts,
    cardSubtotals,
    totalPayments: payments.length,
  };
};

/**
 * Agrupar pagamentos por mês para gráficos.
 * @param {Array} payments
 * @param {number} monthsBack - Quantos meses para trás (padrão 6)
 */
export const groupPaymentsByMonth = (payments, monthsBack = 6) => {
  const now = new Date();
  const monthlyData = {};

  for (let i = monthsBack - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    monthlyData[key] = { cash: 0, pix: 0, card: 0, total: 0, count: 0 };
  }

  payments.forEach(p => {
    const key = p.paymentDate?.slice(0, 7); // YYYY-MM
    if (!key || !monthlyData[key]) return;

    const effective =
      p.paymentMethod === 'card' && p.cardHasInterest && p.cardFinalAmount != null
        ? p.cardFinalAmount
        : p.amountCharged;

    monthlyData[key][p.paymentMethod] = (monthlyData[key][p.paymentMethod] || 0) + effective;
    monthlyData[key].total += effective;
    monthlyData[key].count += 1;
  });

  return monthlyData;
};
